import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  Calendar,
  Clock,
  MapPin,
  Wifi,
  DollarSign,
  XCircle,
  CheckCircle,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  FlaskConical,
} from 'lucide-react';
import { api } from '@/lib/api';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] } },
};

type TabStatus = 'upcoming' | 'completed' | 'cancelled';

const TABS: { key: TabStatus; label: string; apiStatus: string }[] = [
  { key: 'upcoming', label: 'Upcoming', apiStatus: 'booked' },
  { key: 'completed', label: 'Completed', apiStatus: 'completed' },
  { key: 'cancelled', label: 'Cancelled', apiStatus: 'cancelled' },
];

interface Appointment {
  id: string;
  study_id: string;
  study_title: string;
  study_compensation_amount: number;
  study_compensation_type: string;
  study_location: string | null;
  study_is_remote: boolean;
  slot_start_time: string;
  slot_end_time: string;
  status: string;
  booked_at: string;
}

export default function MyAppointments() {
  const [activeTab, setActiveTab] = useState<TabStatus>('upcoming');
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [cancelling, setCancelling] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const tab = TABS.find((t) => t.key === activeTab)!;
        const data = await api.getMyAppointments({ page, status: tab.apiStatus });
        if (!mounted) return;
        setAppointments(data.data ?? []);
        setTotalPages(data.total_pages ?? 1);
      } catch {
        // silently fail
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [activeTab, page]);

  const handleCancel = async (id: string) => {
    if (!confirm('Are you sure you want to cancel this appointment? This may affect your reliability score.')) return;
    setCancelling(id);
    try {
      await api.cancelAppointment(id);
      setAppointments((prev) => prev.filter((a) => a.id !== id));
      toast.success('Appointment cancelled');
    } catch (err: any) {
      toast.error(err.message || 'Failed to cancel');
    } finally {
      setCancelling(null);
    }
  };

  const handleTabChange = (tab: TabStatus) => {
    setActiveTab(tab);
    setPage(1);
  };

  return (
    <motion.div variants={fadeUp} initial="hidden" animate="visible" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="page-header">My Appointments</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Manage your upcoming, completed, and cancelled appointments
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => handleTabChange(tab.key)}
            className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
              activeTab === tab.key
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="card p-5 animate-pulse">
              <div className="flex gap-4">
                <div className="w-14 h-14 bg-gray-200 dark:bg-gray-800 rounded-xl" />
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-gray-200 dark:bg-gray-800 rounded w-1/2" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : appointments.length === 0 ? (
        <div className="text-center py-16">
          <Calendar className="w-14 h-14 text-gray-300 dark:text-gray-700 mx-auto mb-4" />
          <p className="text-lg font-semibold text-gray-600 dark:text-gray-400 mb-1">
            No {activeTab} appointments
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500">
            {activeTab === 'upcoming'
              ? 'Browse studies and book your first appointment!'
              : `Your ${activeTab} appointments will appear here`}
          </p>
          {activeTab === 'upcoming' && (
            <Link to="/studies" className="btn-primary inline-flex mt-4">
              Browse Studies
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {appointments.map((apt) => {
            const startDate = new Date(apt.slot_start_time);
            const endDate = new Date(apt.slot_end_time);
            const isUpcoming = activeTab === 'upcoming';
            const isPast = startDate < new Date();

            return (
              <motion.div
                key={apt.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="card p-5"
              >
                <div className="flex items-start gap-4">
                  {/* Date badge */}
                  <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-brand-50 dark:bg-brand-900/20 flex flex-col items-center justify-center">
                    <span className="text-[10px] font-bold text-brand-500 uppercase">
                      {startDate.toLocaleDateString('en-US', { month: 'short' })}
                    </span>
                    <span className="text-xl font-bold text-brand-600 dark:text-brand-400 leading-none">
                      {startDate.getDate()}
                    </span>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <Link
                          to={`/studies/${apt.study_id}`}
                          className="text-base font-semibold hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
                        >
                          {apt.study_title}
                        </Link>
                        <div className="flex items-center gap-3 mt-1 flex-wrap">
                          <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                            <Clock className="w-3 h-3" />
                            {startDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                            {' '}&ndash;{' '}
                            {endDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                          </span>
                          <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                            {apt.study_is_remote ? (
                              <><Wifi className="w-3 h-3 text-accent-500" /> Remote</>
                            ) : (
                              <><MapPin className="w-3 h-3" /> {apt.study_location || 'On campus'}</>
                            )}
                          </span>
                        </div>
                      </div>

                      {/* Compensation */}
                      <div className="flex-shrink-0 text-right">
                        <span className="text-lg font-bold text-success-500">${apt.study_compensation_amount}</span>
                        <p className="text-[10px] text-gray-400 capitalize">{apt.study_compensation_type}</p>
                      </div>
                    </div>

                    {/* Status + actions */}
                    <div className="flex items-center gap-3 mt-3">
                      <AppointmentStatusBadge status={apt.status} />

                      {isUpcoming && !isPast && (
                        <button
                          onClick={() => handleCancel(apt.id)}
                          disabled={cancelling === apt.id}
                          className="text-xs font-medium text-danger-500 hover:text-danger-600 flex items-center gap-1 ml-auto"
                        >
                          {cancelling === apt.id ? (
                            <div className="w-3 h-3 border-2 border-danger-300 border-t-danger-500 rounded-full animate-spin" />
                          ) : (
                            <XCircle className="w-3.5 h-3.5" />
                          )}
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="btn-ghost disabled:opacity-40"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="btn-ghost disabled:opacity-40"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </motion.div>
  );
}

function AppointmentStatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'booked':
    case 'confirmed':
      return (
        <span className="badge-brand">
          <Calendar className="w-3 h-3" /> {status === 'confirmed' ? 'Confirmed' : 'Booked'}
        </span>
      );
    case 'completed':
      return (
        <span className="badge-success">
          <CheckCircle className="w-3 h-3" /> Completed
        </span>
      );
    case 'no_show':
      return (
        <span className="badge-danger">
          <AlertTriangle className="w-3 h-3" /> No Show
        </span>
      );
    case 'cancelled':
      return (
        <span className="badge bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
          <XCircle className="w-3 h-3" /> Cancelled
        </span>
      );
    default:
      return <span className="badge">{status}</span>;
  }
}
